/**
 * Storage Service
 * Provides typed access to localStorage and sessionStorage
 * with centralized key management and error handling
 */

/**
 * Storage keys used throughout the application
 */
export enum StorageKey {
  // Authentication
  TOKEN = 'token',
  MANAGED_TOKEN = 'managed_token',
  ORGANIZATION_SECRET = 'organizationSecret',

  // User preferences
  ROLE = 'role',
  VERSION = 'version',

  // Onboarding
  ONBOARDING_PATH = 'onboardingPath',
  SETUP_AUTH_TASK_SECRET = 'setupAuthTaskSecret',
  PASSWORD_CHANGE = 'passwordChange',
  USERNAME = 'username',

  // Shopify integration
  SHOPIFY_SHOP = 'shopify_shop',
  SHOPIFY_TOKEN = 'shopify_token',
  SHOPIFY_REDIRECT_URL = 'shopify_redirect_url',

  // Analytics
  FBQ_TRACK = 'fbqTrack',
}

/**
 * Storage type enum
 */
export enum StorageType {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage',
}

/**
 * Check if storage is available
 *
 * @param type - The type of storage to check
 * @returns Whether the storage is available
 */
const isStorageAvailable = (type: StorageType): boolean => {
  try {
    const storage = type === StorageType.LOCAL ? localStorage : sessionStorage;
    const testKey = '__storage_test__';
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get an item from storage
 *
 * @param key - The storage key
 * @param type - The storage type (localStorage or sessionStorage)
 * @returns The stored value or null if not found
 */
export const getItem = (
  key: StorageKey | string,
  type: StorageType = StorageType.LOCAL,
): string | null => {
  if (!isStorageAvailable(type)) {
    return null;
  }

  try {
    const storage = type === StorageType.LOCAL ? localStorage : sessionStorage;
    return storage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Set an item in storage
 *
 * @param key - The storage key
 * @param value - The value to store
 * @param type - The storage type (localStorage or sessionStorage)
 * @returns Whether the operation was successful
 */
export const setItem = (
  key: StorageKey | string,
  value: string,
  type: StorageType = StorageType.LOCAL,
): boolean => {
  if (!isStorageAvailable(type)) {
    return false;
  }

  try {
    const storage = type === StorageType.LOCAL ? localStorage : sessionStorage;
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Remove an item from storage
 *
 * @param key - The storage key
 * @param type - The storage type (localStorage or sessionStorage)
 * @returns Whether the operation was successful
 */
export const removeItem = (
  key: StorageKey | string,
  type: StorageType = StorageType.LOCAL,
): boolean => {
  if (!isStorageAvailable(type)) {
    return false;
  }

  try {
    const storage = type === StorageType.LOCAL ? localStorage : sessionStorage;
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

/**
 * Clear all items from storage
 *
 * @param type - The storage type (localStorage or sessionStorage)
 * @returns Whether the operation was successful
 */
export const clearAll = (type: StorageType = StorageType.LOCAL): boolean => {
  if (!isStorageAvailable(type)) {
    return false;
  }

  try {
    const storage = type === StorageType.LOCAL ? localStorage : sessionStorage;
    storage.clear();
    return true;
  } catch {
    return false;
  }
};

// ============================================================================
// Token Management
// ============================================================================

/**
 * Get the authentication token
 *
 * @returns The auth token or null if not found
 */
export const getToken = (): string | null => {
  return getItem(StorageKey.TOKEN);
};

/**
 * Set the authentication token
 *
 * @param token - The auth token to store
 * @returns Whether the operation was successful
 */
export const setToken = (token: string): boolean => {
  return setItem(StorageKey.TOKEN, token);
};

/**
 * Remove the authentication token
 *
 * @returns Whether the operation was successful
 */
export const removeToken = (): boolean => {
  return removeItem(StorageKey.TOKEN);
};

// ============================================================================
// Managed Token Management
// ============================================================================

/**
 * Get the managed token
 *
 * @returns The managed token or null if not found
 */
export const getManagedToken = (): string | null => {
  return getItem(StorageKey.MANAGED_TOKEN);
};

/**
 * Set the managed token
 *
 * @param token - The managed token to store
 * @returns Whether the operation was successful
 */
export const setManagedToken = (token: string): boolean => {
  return setItem(StorageKey.MANAGED_TOKEN, token);
};

/**
 * Remove the managed token
 *
 * @returns Whether the operation was successful
 */
export const removeManagedToken = (): boolean => {
  return removeItem(StorageKey.MANAGED_TOKEN);
};

// ============================================================================
// Role Management
// ============================================================================

/**
 * Get the user role
 *
 * @returns The role or null if not found
 */
export const getRole = (): string | null => {
  return getItem(StorageKey.ROLE);
};

/**
 * Set the user role
 *
 * @param role - The role to store
 * @returns Whether the operation was successful
 */
export const setRole = (role: string): boolean => {
  return setItem(StorageKey.ROLE, role);
};

/**
 * Remove the user role
 *
 * @returns Whether the operation was successful
 */
export const removeRole = (): boolean => {
  return removeItem(StorageKey.ROLE);
};

// ============================================================================
// Version Management
// ============================================================================

/**
 * Get the application version
 *
 * @returns The version or null if not found
 */
export const getVersion = (): string | null => {
  return getItem(StorageKey.VERSION);
};

/**
 * Set the application version
 *
 * @param version - The version to store
 * @returns Whether the operation was successful
 */
export const setVersion = (version: string): boolean => {
  return setItem(StorageKey.VERSION, version);
};

/**
 * Remove the application version
 *
 * @returns Whether the operation was successful
 */
export const removeVersion = (): boolean => {
  return removeItem(StorageKey.VERSION);
};

// ============================================================================
// Onboarding Path Management
// ============================================================================

/**
 * Get the onboarding path
 *
 * @returns The onboarding path or null if not found
 */
export const getOnboardingPath = (): string | null => {
  return getItem(StorageKey.ONBOARDING_PATH);
};

/**
 * Set the onboarding path
 *
 * @param path - The onboarding path to store
 * @returns Whether the operation was successful
 */
export const setOnboardingPath = (path: string): boolean => {
  return setItem(StorageKey.ONBOARDING_PATH, path);
};

/**
 * Remove the onboarding path
 *
 * @returns Whether the operation was successful
 */
export const removeOnboardingPath = (): boolean => {
  return removeItem(StorageKey.ONBOARDING_PATH);
};

// ============================================================================
// Setup Auth Task Secret Management
// ============================================================================

/**
 * Get the setup auth task secret
 *
 * @returns The secret or null if not found
 */
export const getSetupAuthTaskSecret = (): string | null => {
  return getItem(StorageKey.SETUP_AUTH_TASK_SECRET);
};

/**
 * Set the setup auth task secret
 *
 * @param secret - The secret to store
 * @returns Whether the operation was successful
 */
export const setSetupAuthTaskSecret = (secret: string): boolean => {
  return setItem(StorageKey.SETUP_AUTH_TASK_SECRET, secret);
};

/**
 * Remove the setup auth task secret
 *
 * @returns Whether the operation was successful
 */
export const removeSetupAuthTaskSecret = (): boolean => {
  return removeItem(StorageKey.SETUP_AUTH_TASK_SECRET);
};

/**
 * Check if setup auth task secret exists
 *
 * @returns Whether the secret exists
 */
export const hasSetupAuthTaskSecret = (): boolean => {
  return !!getSetupAuthTaskSecret();
};

// ============================================================================
// Password Change Token Management
// ============================================================================

/**
 * Get the password change token
 *
 * @returns The token or null if not found
 */
export const getPasswordChangeToken = (): string | null => {
  return getItem(StorageKey.PASSWORD_CHANGE);
};

/**
 * Set the password change token
 *
 * @param token - The token to store
 * @returns Whether the operation was successful
 */
export const setPasswordChangeToken = (token: string): boolean => {
  return setItem(StorageKey.PASSWORD_CHANGE, token);
};

/**
 * Remove the password change token
 *
 * @returns Whether the operation was successful
 */
export const removePasswordChangeToken = (): boolean => {
  return removeItem(StorageKey.PASSWORD_CHANGE);
};

// ============================================================================
// Shopify Integration
// ============================================================================

/**
 * Shopify data interface
 */
interface ShopifyData {
  shop: string | null;
  token: string | null;
  redirectUrl: string | null;
}

/**
 * Get all Shopify data
 *
 * @returns Object containing Shopify shop, token, and redirect URL
 */
export const getShopifyData = (): ShopifyData => {
  return {
    shop: getItem(StorageKey.SHOPIFY_SHOP),
    token: getItem(StorageKey.SHOPIFY_TOKEN),
    redirectUrl: getItem(StorageKey.SHOPIFY_REDIRECT_URL),
  };
};

/**
 * Set Shopify data
 *
 * @param data - The Shopify data to store
 * @returns Whether all operations were successful
 */
export const setShopifyData = (data: Partial<ShopifyData>): boolean => {
  let success = true;
  if (data.shop !== undefined) {
    success = setItem(StorageKey.SHOPIFY_SHOP, data.shop ?? '') && success;
  }
  if (data.token !== undefined) {
    success = setItem(StorageKey.SHOPIFY_TOKEN, data.token ?? '') && success;
  }
  if (data.redirectUrl !== undefined) {
    success =
      setItem(StorageKey.SHOPIFY_REDIRECT_URL, data.redirectUrl ?? '') &&
      success;
  }
  return success;
};

/**
 * Remove all Shopify data
 *
 * @returns Whether all operations were successful
 */
export const removeShopifyData = (): boolean => {
  const shop = removeItem(StorageKey.SHOPIFY_SHOP);
  const token = removeItem(StorageKey.SHOPIFY_TOKEN);
  const redirectUrl = removeItem(StorageKey.SHOPIFY_REDIRECT_URL);
  return shop && token && redirectUrl;
};

// ============================================================================
// Organization Secret Management
// ============================================================================

/**
 * Get the organization secret
 *
 * @returns The organization secret or null if not found
 */
export const getOrganizationSecret = (): string | null => {
  return getItem(StorageKey.ORGANIZATION_SECRET);
};

/**
 * Set the organization secret
 *
 * @param secret - The organization secret to store
 * @returns Whether the operation was successful
 */
export const setOrganizationSecret = (secret: string): boolean => {
  return setItem(StorageKey.ORGANIZATION_SECRET, secret);
};

/**
 * Remove the organization secret
 *
 * @returns Whether the operation was successful
 */
export const removeOrganizationSecret = (): boolean => {
  return removeItem(StorageKey.ORGANIZATION_SECRET);
};

// ============================================================================
// FBQ Track Management
// ============================================================================

/**
 * Get the FBQ track value
 *
 * @returns The FBQ track value or null if not found
 */
export const getFbqTrack = (): string | null => {
  return getItem(StorageKey.FBQ_TRACK);
};

/**
 * Set the FBQ track value
 *
 * @param value - The FBQ track value to store
 * @returns Whether the operation was successful
 */
export const setFbqTrack = (value: string): boolean => {
  return setItem(StorageKey.FBQ_TRACK, value);
};

/**
 * Remove the FBQ track value
 *
 * @returns Whether the operation was successful
 */
export const removeFbqTrack = (): boolean => {
  return removeItem(StorageKey.FBQ_TRACK);
};

// ============================================================================
// Username Management
// ============================================================================

/**
 * Get the stored username
 *
 * @returns The username or null if not found
 */
export const getUsername = (): string | null => {
  return getItem(StorageKey.USERNAME);
};

/**
 * Set the username
 *
 * @param username - The username to store
 * @returns Whether the operation was successful
 */
export const setUsername = (username: string): boolean => {
  return setItem(StorageKey.USERNAME, username);
};

/**
 * Remove the username
 *
 * @returns Whether the operation was successful
 */
export const removeUsername = (): boolean => {
  return removeItem(StorageKey.USERNAME);
};

// ============================================================================
// Auth Cleanup
// ============================================================================

/**
 * Clear all authentication-related storage
 * Useful for logout operations
 *
 * @returns Whether all operations were successful
 */
export const clearAuthStorage = (): boolean => {
  const results = [
    removeToken(),
    removeManagedToken(),
    removeOrganizationSecret(),
    removeRole(),
    removeOnboardingPath(),
    removeSetupAuthTaskSecret(),
    removePasswordChangeToken(),
    removeFbqTrack(),
    removeUsername(),
  ];
  return results.every(Boolean);
};

/**
 * Clear onboarding-related storage
 *
 * @returns Whether all operations were successful
 */
export const clearOnboardingStorage = (): boolean => {
  const results = [removeOnboardingPath(), removeFbqTrack()];
  return results.every(Boolean);
};

// ============================================================================
// Default Export
// ============================================================================

export default {
  // Enums
  StorageKey,
  StorageType,

  // Generic methods
  getItem,
  setItem,
  removeItem,
  clearAll,

  // Token methods
  getToken,
  setToken,
  removeToken,

  // Managed token methods
  getManagedToken,
  setManagedToken,
  removeManagedToken,

  // Role methods
  getRole,
  setRole,
  removeRole,

  // Version methods
  getVersion,
  setVersion,
  removeVersion,

  // Onboarding path methods
  getOnboardingPath,
  setOnboardingPath,
  removeOnboardingPath,

  // Setup auth task secret methods
  getSetupAuthTaskSecret,
  setSetupAuthTaskSecret,
  removeSetupAuthTaskSecret,
  hasSetupAuthTaskSecret,

  // Password change methods
  getPasswordChangeToken,
  setPasswordChangeToken,
  removePasswordChangeToken,

  // Shopify methods
  getShopifyData,
  setShopifyData,
  removeShopifyData,

  // Organization secret methods
  getOrganizationSecret,
  setOrganizationSecret,
  removeOrganizationSecret,

  // FBQ track methods
  getFbqTrack,
  setFbqTrack,
  removeFbqTrack,

  // Username methods
  getUsername,
  setUsername,
  removeUsername,

  // Cleanup methods
  clearAuthStorage,
  clearOnboardingStorage,
};
